import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/client';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton,
  Collapse, CircularProgress, Alert, Tooltip, TextField,
  InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Divider, Button, useTheme, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const VENDOR_COLORS = {
  // snake_case (legacy pre-migration)
  american_eagle:      '#c41230',
  american_eagle_mens: '#a01025',
  aerie:               '#e85d8a',
  chico:               '#7c3aed',
  freepeople:          '#d97706',
  lane_bryant:         '#1d4ed8',
  mast:                '#059669',
  pink:                '#db2777',
  loft:                '#b45309',
  loft_outlet:         '#92400e',
  ann_taylor:          '#78350f',
  ann_taylor_factory:  '#6b3a0f',
  talbots:             '#be123c',
  // customerCode (post-migration)
  BAE:  '#c41230',
  BAM:  '#a01025',
  BAR:  '#e85d8a',
  BC:   '#7c3aed',
  BFP:  '#d97706',
  BLB:  '#1d4ed8',
  BVS:  '#059669',
  BVP:  '#db2777',
  BLF:  '#b45309',
  BLFO: '#92400e',
  BAT:  '#78350f',
  BATF: '#6b3a0f',
  BTL:  '#be123c',
};

const VENDOR_DISPLAY_NAME = {
  // snake_case (legacy pre-migration)
  american_eagle:      'American Eagle',
  american_eagle_mens: 'American Eagle Men',
  aerie:               'Aerie',
  chico:               "Chico's",
  freepeople:          'Free People',
  lane_bryant:         'Lane Bryant',
  mast:                "Victoria's Secret",
  pink:                'Pink',
  loft:                'Ann Taylor Loft',
  loft_outlet:         'Ann Taylor Loft Outlet',
  ann_taylor:          'Ann Taylor',
  ann_taylor_factory:  'Ann Taylor Factory',
  talbots:             'Talbots',
  // customerCode (post-migration)
  BAE:  'American Eagle',
  BAM:  'American Eagle Men',
  BAR:  'Aerie',
  BC:   "Chico's",
  BFP:  'Free People',
  BLB:  'Lane Bryant',
  BVS:  "Victoria's Secret",
  BVP:  'Pink',
  BLF:  'Ann Taylor Loft',
  BLFO: 'Ann Taylor Loft Outlet',
  BAT:  'Ann Taylor',
  BATF: 'Ann Taylor Factory',
  BTL:  'Talbots',
};

const STATUS_COLORS = {
  draft:    '#f59e0b',
  pending:  '#06b6d4',
  accepted: '#10b981',
  rejected: '#ef4444',
};

// Normalise changed_fields — handles old flat-array format and new structured-object format
function parseChangedFields(cf) {
  if (!cf) return { headerChanges: [], lineItemChanges: null };
  if (Array.isArray(cf)) return { headerChanges: cf, lineItemChanges: null };
  return {
    headerChanges:   cf.header_changes   || [],
    lineItemChanges: cf.line_item_changes || null,
  };
}

function countTotalChanges(cf) {
  const { headerChanges, lineItemChanges } = parseChangedFields(cf);
  if (!lineItemChanges) return headerChanges.length;
  return (
    headerChanges.length +
    (lineItemChanges.added   || []).length +
    (lineItemChanges.removed || []).length +
    (lineItemChanges.modified|| []).length
  );
}

// Key fields shown in the added/removed row summary
const LINE_SUMMARY_FIELDS = ['row_number','customer_style_name','customer_style_number','color','size','upc','pieces_to_ship'];

function LineItemSummaryRow({ row }) {
  const vals = LINE_SUMMARY_FIELDS
    .filter(f => row[f] != null && String(row[f]).trim() !== '' && String(row[f]).toLowerCase() !== 'none')
    .map(f => `${f.replace(/_/g,' ')}: ${row[f]}`);
  return (
    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
      {vals.length ? vals.join(' | ') : `Row ${row.row_number ?? '?'}`}
    </Typography>
  );
}

function LineItemChangesSection({ lineItemChanges }) {
  const added    = lineItemChanges?.added    || [];
  const removed  = lineItemChanges?.removed  || [];
  const modified = lineItemChanges?.modified || [];

  if (!added.length && !removed.length && !modified.length) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.72rem', letterSpacing: '0.06em', color: 'text.secondary' }}>
        LINE ITEM CHANGES
      </Typography>

      {added.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ mb: 0.75, border: '1px solid', borderColor: 'success.main', borderRadius: '8px !important', overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '&.Mui-expanded': { minHeight: 36 }, py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCircleOutlineIcon sx={{ color: 'success.main', fontSize: 16 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: 'success.main', fontSize: '0.8rem' }}>
                {added.length} row{added.length !== 1 ? 's' : ''} added
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            {added.map((r, i) => (
              <Box key={i} sx={{ py: 0.4, borderBottom: '1px dashed', borderColor: 'divider' }}>
                <LineItemSummaryRow row={r} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {removed.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ mb: 0.75, border: '1px solid', borderColor: 'error.main', borderRadius: '8px !important', overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '&.Mui-expanded': { minHeight: 36 }, py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RemoveCircleOutlineIcon sx={{ color: 'error.main', fontSize: 16 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: 'error.main', fontSize: '0.8rem' }}>
                {removed.length} row{removed.length !== 1 ? 's' : ''} removed
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            {removed.map((r, i) => (
              <Box key={i} sx={{ py: 0.4, borderBottom: '1px dashed', borderColor: 'divider' }}>
                <LineItemSummaryRow row={r} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {modified.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ mb: 0.75, border: '1px solid', borderColor: 'warning.main', borderRadius: '8px !important', overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '&.Mui-expanded': { minHeight: 36 }, py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditNoteIcon sx={{ color: 'warning.main', fontSize: 16 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.main', fontSize: '0.8rem' }}>
                {modified.length} row{modified.length !== 1 ? 's' : ''} modified
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            {modified.map((m, i) => (
              <Box key={i} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  {String(m.row_number).startsWith('__idx_')
                    ? `Row ${parseInt(String(m.row_number).replace('__idx_', ''), 10) + 1}`
                    : `Row ${m.row_number}`}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: '28%' }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Previous</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>New</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {m.changes.map((c, ci) => (
                        <TableRow key={ci} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>{c.label || c.field}</TableCell>
                          <TableCell>
                            <Box component="span" sx={{ bgcolor: 'error.main', color: 'error.contrastText', px: 0.75, py: 0.2, borderRadius: 0.75, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                              {c.old_value ?? <em style={{ opacity: 0.5 }}>empty</em>}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={{ bgcolor: 'success.main', color: 'success.contrastText', px: 0.75, py: 0.2, borderRadius: 0.75, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                              {c.new_value ?? <em style={{ opacity: 0.5 }}>empty</em>}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

function RevisionTimeline({ poId, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [data, setData]     = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/po-revisions/${poId}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [poId]);

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data)   return null;

  const revisions = data.revisions || [];

  // ── Compare view ──────────────────────────────────────────────────────────
  const renderCompare = () => {
    const revA = revisions.find(r => r.revision_sequence === compareA);
    const revB = revisions.find(r => r.revision_sequence === compareB);
    if (!revA || !revB) return null;

    // Header field compare
    const allFields = Array.from(new Set([
      ...Object.keys(revA.header_snapshot || {}),
      ...Object.keys(revB.header_snapshot || {}),
    ])).filter(f => !['id','header_coordinates','grid_coordinates'].includes(f));

    const changedHeaderFields = allFields.filter(f => {
      const va = String(revA.header_snapshot?.[f] ?? '').trim();
      const vb = String(revB.header_snapshot?.[f] ?? '').trim();
      return va !== vb;
    });

    // Line item compare between the two selected revisions
    const linesA = revA.line_items_snapshot || [];
    const linesB = revB.line_items_snapshot || [];
    const liByRowA = Object.fromEntries(linesA.map(r => [String(r.row_number ?? ''), r]));
    const liByRowB = Object.fromEntries(linesB.map(r => [String(r.row_number ?? ''), r]));
    const allRowKeys = Array.from(new Set([...Object.keys(liByRowA), ...Object.keys(liByRowB)]))
      .sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));

    const addedRows    = allRowKeys.filter(k => !liByRowA[k] && liByRowB[k]);
    const removedRows  = allRowKeys.filter(k =>  liByRowA[k] && !liByRowB[k]);
    const modifiedRows = allRowKeys
      .filter(k => liByRowA[k] && liByRowB[k])
      .map(k => {
        const changes = Object.keys({ ...liByRowA[k], ...liByRowB[k] })
          .filter(f => !['purchase_order_id'].includes(f))
          .filter(f => {
            const va = String(liByRowA[k]?.[f] ?? '').trim();
            const vb = String(liByRowB[k]?.[f] ?? '').trim();
            return va !== vb;
          })
          .map(f => ({ field: f, label: f.replace(/_/g,' '), oldValue: liByRowA[k][f], newValue: liByRowB[k][f] }));
        return changes.length ? { rowNumber: k, changes } : null;
      })
      .filter(Boolean);

    const hasAnyDiff = changedHeaderFields.length || addedRows.length || removedRows.length || modifiedRows.length;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          Comparing Rev&nbsp;{compareA} vs Rev&nbsp;{compareB}
          {!hasAnyDiff && ' — no differences found'}
        </Typography>

        {changedHeaderFields.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75, fontSize: '0.72rem', letterSpacing: '0.06em', color: 'text.secondary' }}>
              HEADER FIELDS
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rev {compareA}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rev {compareB}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changedHeaderFields.map(f => (
                    <TableRow key={f}>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem' }}>{f.replace(/_/g,' ')}</TableCell>
                      <TableCell>
                        <Box component="span" sx={{ bgcolor: 'error.main', color: 'error.contrastText', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {String(revA.header_snapshot?.[f] ?? '') || <em style={{ opacity: 0.5 }}>—</em>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box component="span" sx={{ bgcolor: 'success.main', color: 'success.contrastText', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {String(revB.header_snapshot?.[f] ?? '') || <em style={{ opacity: 0.5 }}>—</em>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {(addedRows.length > 0 || removedRows.length > 0 || modifiedRows.length > 0) && (
          <LineItemChangesSection lineItemChanges={{
            added:    addedRows.map(k => liByRowB[k]),
            removed:  removedRows.map(k => liByRowA[k]),
            modified: modifiedRows.map(m => ({
              row_number: m.rowNumber,
              changes: m.changes.map(c => ({ field: c.field, label: c.label, old_value: c.oldValue, new_value: c.newValue })),
            })),
          }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          PO {data.po_number} — {revisions.length} revision{revisions.length !== 1 ? 's' : ''}
        </Typography>
        {revisions.length >= 2 && (
          <Button
            size="small"
            variant={compareMode ? 'contained' : 'outlined'}
            startIcon={<CompareArrowsIcon />}
            onClick={() => { setCompareMode(v => !v); setCompareA(null); setCompareB(null); }}
          >
            {compareMode ? 'Close Compare' : 'Compare Revisions'}
          </Button>
        )}
        {compareMode && (
          <>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Revision A</InputLabel>
              <Select value={compareA ?? ''} label="Revision A" onChange={e => setCompareA(Number(e.target.value))}>
                {revisions.map(r => (
                  <MenuItem key={r.revision_sequence} value={r.revision_sequence}>
                    Rev {r.revision_sequence} — {r.revision_number || '(no version)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Revision B</InputLabel>
              <Select value={compareB ?? ''} label="Revision B" onChange={e => setCompareB(Number(e.target.value))}>
                {revisions.map(r => (
                  <MenuItem key={r.revision_sequence} value={r.revision_sequence}>
                    Rev {r.revision_sequence} — {r.revision_number || '(no version)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {compareMode && compareA && compareB && compareA !== compareB && renderCompare()}

      {/* Timeline */}
      <Box sx={{ position: 'relative', pl: 3 }}>
        {/* Vertical spine */}
        <Box sx={{
          position: 'absolute', left: 10, top: 0, bottom: 0,
          width: 2, bgcolor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)',
        }} />

        {revisions.map((rev, idx) => {
          const isLatest = idx === revisions.length - 1;
          const { headerChanges, lineItemChanges } = parseChangedFields(rev.changed_fields);
          const totalChanges = countTotalChanges(rev.changed_fields);
          return (
            <Box key={rev.id} sx={{ mb: 2.5, position: 'relative' }}>
              {/* Timeline dot */}
              <Box sx={{
                position: 'absolute', left: -18, top: 6,
                width: 14, height: 14, borderRadius: '50%',
                bgcolor: isLatest ? 'primary.main' : (isDark ? 'rgba(148,163,184,0.4)' : 'rgba(15,23,42,0.25)'),
                border: `2px solid ${isLatest ? '#818cf8' : 'transparent'}`,
              }} />

              <Paper
                variant="outlined"
                sx={{
                  p: 2, borderRadius: 2,
                  borderColor: isLatest
                    ? (isDark ? 'rgba(99,102,241,0.5)' : 'rgba(79,70,229,0.3)')
                    : undefined,
                  bgcolor: isLatest
                    ? (isDark ? 'rgba(99,102,241,0.07)' : 'rgba(79,70,229,0.03)')
                    : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                  <Chip
                    size="small"
                    label={`Sequence ${rev.revision_sequence}`}
                    color={isLatest ? 'primary' : 'default'}
                    variant={isLatest ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                  />
                  {rev.revision_number && (
                    <Chip
                      size="small"
                      label={`Version: ${rev.revision_number}`}
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  )}
                  {isLatest && <Chip size="small" label="Latest" color="success" />}
                  {totalChanges > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {headerChanges.length > 0 && (
                        <Chip size="small" label={`${headerChanges.length} header field${headerChanges.length !== 1 ? 's' : ''}`} color="warning" variant="outlined" />
                      )}
                      {lineItemChanges && (lineItemChanges.added||[]).length > 0 && (
                        <Chip size="small" label={`+${lineItemChanges.added.length} rows`} color="success" variant="outlined" />
                      )}
                      {lineItemChanges && (lineItemChanges.removed||[]).length > 0 && (
                        <Chip size="small" label={`-${lineItemChanges.removed.length} rows`} color="error" variant="outlined" />
                      )}
                      {lineItemChanges && (lineItemChanges.modified||[]).length > 0 && (
                        <Chip size="small" label={`~${lineItemChanges.modified.length} rows modified`} color="warning" variant="outlined" />
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {rev.uploaded_at ? new Date(rev.uploaded_at).toLocaleString() : ''}
                  </Typography>
                </Box>

                {/* Header field diff table */}
                {headerChanges.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.75, fontSize: '0.72rem', letterSpacing: '0.06em', color: 'text.secondary' }}>
                      HEADER CHANGES
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: '28%' }}>Field</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Previous</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>New</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {headerChanges.map((cf, ci) => (
                            <TableRow key={ci} sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                                {cf.label || cf.field}
                              </TableCell>
                              <TableCell>
                                <Box component="span" sx={{
                                  bgcolor: 'error.main', color: 'error.contrastText',
                                  px: 0.75, py: 0.2, borderRadius: 0.75, fontSize: '0.75rem', fontFamily: 'monospace',
                                }}>
                                  {cf.old_value ?? <em style={{ opacity: 0.5 }}>empty</em>}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box component="span" sx={{
                                  bgcolor: 'success.main', color: 'success.contrastText',
                                  px: 0.75, py: 0.2, borderRadius: 0.75, fontSize: '0.75rem', fontFamily: 'monospace',
                                }}>
                                  {cf.new_value ?? <em style={{ opacity: 0.5 }}>empty</em>}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Line item changes */}
                {lineItemChanges && <LineItemChangesSection lineItemChanges={lineItemChanges} />}

                {totalChanges === 0 && idx > 0 && (
                  <Typography variant="caption" color="text.secondary">No changes recorded for this revision.</Typography>
                )}
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function PoRevisionRow({ po, navigate }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [open, setOpen] = useState(false);
  const vendorColor = VENDOR_COLORS[po.vendor] || '#6366f1';
  const latestChangedCount = countTotalChanges(po.latest_changed_fields);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 0 : undefined } }}
        onClick={() => setOpen(v => !v)}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              px: 1.25, py: 0.3, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700,
              bgcolor: `${vendorColor}22`, color: vendorColor,
            }}>
              {VENDOR_DISPLAY_NAME[po.vendor] || (po.vendor || '').replace(/_/g, ' ').toUpperCase()}
            </Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.875rem' }}>
              {po.customer_po_number}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`Version ${po.po_version_number || '—'}`}
            variant="outlined"
            sx={{ fontFamily: 'monospace' }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`${po.rev_count} revisions`}
            color="primary"
            sx={{ fontWeight: 700 }}
          />
        </TableCell>
        <TableCell>
          {latestChangedCount > 0
            ? <Chip size="small" label={`${latestChangedCount} change${latestChangedCount !== 1 ? 's' : ''} in last rev`} color="warning" variant="outlined" />
            : <Typography variant="caption" color="text.secondary">No changes in latest rev</Typography>
          }
        </TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          {po.updated_at ? new Date(po.updated_at).toLocaleDateString() : '—'}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={po.status || 'draft'}
            sx={{
              fontWeight: 600,
              bgcolor: `${STATUS_COLORS[po.status] || '#94a3b8'}22`,
              color: STATUS_COLORS[po.status] || '#94a3b8',
            }}
          />
        </TableCell>
        <TableCell>
          <Tooltip title="Open in Audit">
            <IconButton
              size="small"
              onClick={e => { e.stopPropagation(); navigate(`/audit/${po.id}`); }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <RevisionTimeline poId={po.id} />
            <Divider />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function RevisionHistoryPage() {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [vendor, setVendor]   = useState('');
  const [page, setPage]       = useState(0);
  const LIMIT = 25;

  const fetchData = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { limit: LIMIT, offset: page * LIMIT, min_revisions: 2 };
    if (vendor)             params.vendor    = vendor;
    if (search.trim())      params.po_number = search.trim();
    axios.get(`${API_BASE_URL}/api/po-revisions/`, { params })
      .then(r => { setRows(r.data.data || []); setTotal(r.data.total || 0); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [page, vendor, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const vendors = [
    { value: 'american_eagle',      label: 'American Eagle'         },
    { value: 'american_eagle_mens', label: 'American Eagle Men'     },
    { value: 'aerie',               label: 'Aerie'                  },
    { value: 'chico',               label: "Chico's"                },
    { value: 'freepeople',          label: 'Free People'            },
    { value: 'lane_bryant',         label: 'Lane Bryant'            },
    { value: 'mast',                label: "Victoria's Secret"      },
    { value: 'pink',                label: 'Pink'                   },
    { value: 'loft',                label: 'Ann Taylor Loft'        },
    { value: 'loft_outlet',         label: 'Ann Taylor Loft Outlet' },
    { value: 'ann_taylor',          label: 'Ann Taylor'             },
    { value: 'ann_taylor_factory',  label: 'Ann Taylor Factory'     },
    { value: 'talbots',             label: 'Talbots'                },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <HistoryIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4" fontWeight={700}>
            Revision History
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Purchase orders that have been re-uploaded with a new revision number. Expand a row to see
          the full timeline and field-level diffs between each revision.
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search PO number…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Customer</InputLabel>
          <Select value={vendor} label="Customer" onChange={e => { setVendor(e.target.value); setPage(0); }}>
            <MenuItem value="">All customers</MenuItem>
            {vendors.map(v => (
              <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {total} PO{total !== 1 ? 's' : ''} with revisions
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading revision history…
          </Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No revisions found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Upload a PO with a new revision number to see it tracked here.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>PO Number</TableCell>
                  <TableCell>Current Version</TableCell>
                  <TableCell>Revisions</TableCell>
                  <TableCell>Latest Change</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ width: 52 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(po => (
                  <PoRevisionRow key={po.id} po={po} navigate={navigate} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
            </Typography>
            <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="small" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Box>
        </>
      )}
    </Box>
  );
}
